import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, CheckCircle2, AlertCircle, Globe } from "lucide-react";
import { useLang } from "@/components/LanguageProvider";
import { postToBoothWebhook } from "@/lib/n8n";

const ROLE_KEYS = ["buyer", "trader", "investor", "partner", "other"];
const ROLE_API_VALUES = {
  buyer: "Buyer",
  trader: "Trader",
  investor: "Investor",
  partner: "Partner",
  other: "Other",
};

export default function BoothIntake() {
  const { t, lang, toggle, isRtl } = useLang();
  const formRef = useRef(null);
  const nameInputRef = useRef(null);
  const resetTimerRef = useRef(null);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const meta = document.querySelector('meta[name="robots"]');
    const previous = meta ? meta.getAttribute("content") : null;
    const created = !meta;
    const tag = meta || document.createElement("meta");
    tag.setAttribute("name", "robots");
    tag.setAttribute("content", "noindex,nofollow");
    if (created) document.head.appendChild(tag);
    return () => {
      if (created) {
        tag.parentNode?.removeChild(tag);
      } else if (previous != null) {
        tag.setAttribute("content", previous);
      } else {
        tag.removeAttribute("content");
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  const resetToIdle = () => {
    setStatus("idle");
    setErrorMsg("");
    formRef.current?.reset();
    nameInputRef.current?.focus();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");

    const data = Object.fromEntries(new FormData(formRef.current).entries());
    const roleKey = (data.role || "").toString();
    const payload = {
      source: "FlowWhiteBooth",
      event: "FlowWhiteBooth",
      name: (data.name || "").toString().trim(),
      role: ROLE_API_VALUES[roleKey] || "",
      company: (data.company || "").toString().trim(),
      email: (data.email || "").toString().trim(),
      mobile: (data.mobile || "").toString().trim().replace(/^\+/, ""),
      message: (data.message || "").toString().trim(),
      attribution: {
        utm_source: "booth",
        landing_path: "/booth",
      },
      submitted_at: new Date().toISOString(),
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      ip: "",
    };

    try {
      await postToBoothWebhook(payload);
      setStatus("sent");
      formRef.current?.reset();
      resetTimerRef.current = setTimeout(resetToIdle, 8000);
    } catch (err) {
      console.error("Booth webhook failed", err);
      setErrorMsg(err?.message || "Unknown error");
      setStatus("error");
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-white/15 focus:border-brand-teal text-white placeholder-white/30 py-4 px-1 text-base md:text-lg outline-none transition-colors";

  const b = t.booth;

  return (
    <div
      data-testid="booth-page"
      className="min-h-screen bg-brand-navy text-white flex flex-col"
    >
      <header className="px-6 md:px-10 pt-8 md:pt-10 flex items-center justify-between">
        <img
          src="/logo/flowwhite-logo-light.png"
          alt="FlowWhite"
          className="h-9 md:h-10 w-auto"
        />
        <button
          type="button"
          onClick={toggle}
          data-testid="booth-lang-toggle"
          className="inline-flex items-center gap-2 text-xs md:text-sm uppercase tracking-[0.22em] text-white/70 hover:text-white border border-white/15 hover:border-white/30 rounded-full px-4 py-2 transition-colors"
        >
          <Globe size={14} strokeWidth={1.6} />
          {lang === "en" ? "العربية" : "English"}
        </button>
      </header>

      <main className="flex-1 px-6 md:px-10 py-10 md:py-16 flex items-start justify-center">
        <div className="w-full max-w-xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-brand-teal" />
            <span className="text-[11px] uppercase tracking-[0.28em] text-brand-teal font-semibold">
              {b.kicker}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl leading-[1.05] font-medium tracking-tight">
            {b.title}
          </h1>
          <p className={`mt-4 text-white/65 text-base md:text-lg leading-relaxed ${isRtl ? "text-right" : "text-left"}`}>
            {b.subtitle}
          </p>

          <form
            ref={formRef}
            onSubmit={onSubmit}
            data-testid="booth-form"
            className="mt-10 rounded-2xl border border-white/10 bg-brand-surface p-6 md:p-9"
          >
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label htmlFor="booth-name" className="block text-[10px] uppercase tracking-[0.22em] text-white/45 font-semibold">
                  {b.labels.name}
                </label>
                <input
                  ref={nameInputRef}
                  id="booth-name"
                  name="name"
                  required
                  autoComplete="name"
                  data-testid="booth-input-name"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="booth-email" className="block text-[10px] uppercase tracking-[0.22em] text-white/45 font-semibold">
                  {b.labels.email}
                </label>
                <input
                  id="booth-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  data-testid="booth-input-email"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="booth-mobile" className="block text-[10px] uppercase tracking-[0.22em] text-white/45 font-semibold">
                  {b.labels.mobile}
                </label>
                <input
                  id="booth-mobile"
                  name="mobile"
                  type="tel"
                  autoComplete="tel"
                  dir="ltr"
                  data-testid="booth-input-mobile"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="booth-company" className="block text-[10px] uppercase tracking-[0.22em] text-white/45 font-semibold">
                  {b.labels.company}
                </label>
                <input
                  id="booth-company"
                  name="company"
                  autoComplete="organization"
                  data-testid="booth-input-company"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="booth-role" className="block text-[10px] uppercase tracking-[0.22em] text-white/45 font-semibold">
                  {b.labels.role}
                </label>
                <select
                  id="booth-role"
                  name="role"
                  required
                  defaultValue=""
                  data-testid="booth-input-role"
                  className={inputClass + " appearance-none bg-brand-surface"}
                >
                  <option value="" disabled>
                    {isRtl ? "اختر..." : "Select..."}
                  </option>
                  {b.labels.role_options.map((opt, i) => (
                    <option key={ROLE_KEYS[i]} value={ROLE_KEYS[i]} className="bg-brand-surface">
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="booth-message" className="block text-[10px] uppercase tracking-[0.22em] text-white/45 font-semibold">
                  {b.labels.message}
                </label>
                <textarea
                  id="booth-message"
                  name="message"
                  rows={3}
                  data-testid="booth-input-message"
                  className={inputClass + " resize-none"}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={status === "sending" || status === "sent"}
              data-testid="booth-submit-btn"
              className="group mt-9 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-brand-teal hover:bg-brand-tealDark disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 rounded-full text-base md:text-lg font-medium transition-all hover:-translate-y-0.5"
            >
              {status === "sending" ? b.labels.sending : b.labels.send}
              <ArrowRight size={18} className={`transition-transform group-hover:translate-x-1 ${isRtl ? "rotate-180 group-hover:-translate-x-1" : ""}`} />
            </button>

            {status === "sent" && (
              <div
                data-testid="booth-success"
                className="mt-6 flex items-start gap-3 rounded-lg border border-brand-teal/40 bg-brand-teal/10 px-5 py-4 text-sm md:text-base"
              >
                <CheckCircle2 size={20} className="text-brand-teal mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div>{b.labels.sent}</div>
                  <button
                    type="button"
                    onClick={resetToIdle}
                    data-testid="booth-reset-btn"
                    className="mt-2 text-xs uppercase tracking-[0.22em] text-brand-teal hover:text-white transition-colors"
                  >
                    {b.labels.reset}
                  </button>
                </div>
              </div>
            )}

            {status === "error" && (
              <div
                data-testid="booth-error"
                className="mt-6 flex items-start gap-3 rounded-lg border border-red-500/40 bg-red-500/10 px-5 py-4 text-sm md:text-base"
              >
                <AlertCircle size={20} className="text-red-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div>{b.labels.error}{errorMsg ? `: ${errorMsg}` : ""}</div>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    data-testid="booth-retry-btn"
                    className="mt-2 text-xs uppercase tracking-[0.22em] text-red-300 hover:text-white transition-colors"
                  >
                    {b.labels.retry}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </main>
    </div>
  );
}
